import {
  ProcessedDynamicFilter,
  ProcessedStaticFilter,
} from '@app/data/clients/rbServerSideClient/queries/GetSearchResults/types';
import { Box, ColorSwatch, Input, Text } from '@redbubble/design-system';
import logger from '@redbubble/next/logger';
import { FilterOptionInput } from './FilterOptionInput';
import styles from './Filters.module.scss';
import { useFiltersContext } from './FiltersContext';
import { ExperienceType, FilterExperienceType } from './types';

function isDynamicFilter(
  filter: ProcessedDynamicFilter | ProcessedStaticFilter
): filter is ProcessedDynamicFilter {
  // biome-ignore lint/complexity/useLiteralKeys: useLiteralKeys
  return !!filter['experiences'];
}

const isValidExperience = (
  experience: ExperienceType
): experience is FilterExperienceType => {
  return (
    experience.name === 'control' &&
    ['radio', 'checkbox', 'color'].includes(experience.value)
  );
};

interface FilterExperienceProps {
  filter: ProcessedDynamicFilter | ProcessedStaticFilter;
}

export const FilterExperience = ({ filter }: FilterExperienceProps) => {
  const { handleOptionClick, optimisticOption } = useFiltersContext();

  if (isDynamicFilter(filter)) {
    const experience = filter.experiences[0];

    const { options, label: heading } = filter;

    if (!isValidExperience(experience)) {
      const err = { message: 'Invalid filter experience received' };
      logger.error({ err, experience, message: err.message }, err.message);
      return null;
    }

    if (experience.value === 'color') {
      return (
        <Box className={styles.colorsContainer} marginRight="l">
          {options.map((option) => {
            const ticked =
              optimisticOption?.name === option.name || option.applied;

            if (option.disabled) return null;

            return (
              <div
                role="button"
                onClick={() =>
                  handleOptionClick(option.url, option.name, !option.applied)
                }
                tabIndex={-1}
                key={`${option.name}_${option.label}`}
                title={option.label}
                aria-label={`${option.label}, ${option.name}`}
              >
                <ColorSwatch
                  hexColor={option.hexColor}
                  ticked={ticked}
                  imageUrl={option.imageUrl}
                />
              </div>
            );
          })}
        </Box>
      );
    }

    const isOptimisticGroup = options.some(
      (o) => o.name === optimisticOption?.name
    );

    return (
      <>
        {options.map((option) => {
          return (
            <FilterOptionInput
              variant={experience.value}
              option={option}
              key={`${heading}__${option.name}`}
              optimisticOption={optimisticOption}
              isOptimisticGroup={isOptimisticGroup}
            />
          );
        })}
      </>
    );
  }

  return (
    <>
      {filter.options.map((option) => {
        const checked = optimisticOption
          ? optimisticOption.name === option.name
          : option.applied;
        return (
          <Box
            marginBottom="s"
            key={`static_${option.label}_${option.name}`}
            className={styles.inputWrapper}
            onClick={() => handleOptionClick(option.url, option.name)}
          >
            <Input
              key={`${option.name}_${option.label}`}
              type="radio"
              name={option.name}
              label={option.label}
              checked={checked}
              readOnly
              data-testid={`filter-option-${option.name}`}
            />
            <Text marginLeft="s">{option.label}</Text>
          </Box>
        );
      })}
    </>
  );
};
